import { useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";

const MATCH_MENU_GAP_PX = 4;

type CommodityCurrencyMenuPortalProps = {
  appendTo: HTMLElement | null;
  children: ReactNode;
  controlElement: HTMLElement | null;
  innerProps: object;
  menuPosition?: "absolute" | "fixed";
};

export default function CommodityCurrencyMenuPortal({
  appendTo,
  children,
  controlElement,
  innerProps,
  menuPosition = "fixed",
}: CommodityCurrencyMenuPortalProps) {
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null);

  const updatePosition = useCallback(() => {
    if (!controlElement) return;

    const control = controlElement.getBoundingClientRect();
    const valueCell = controlElement.closest<HTMLElement>(".rfv2-packing-list-table__cell--value");
    const cellRect = valueCell?.getBoundingClientRect();

    setPortalStyle({
      position: menuPosition,
      top: control.bottom - MATCH_MENU_GAP_PX,
      left: cellRect?.left ?? control.left,
      width: cellRect?.width ?? control.width,
      zIndex: 9999,
    });
  }, [controlElement, menuPosition]);

  useLayoutEffect(() => {
    if (!controlElement) return undefined;

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [controlElement, updatePosition]);

  if ((!appendTo && menuPosition !== "fixed") || !portalStyle) {
    return null;
  }

  return createPortal(
    <div className="rfv2-packing-list-currency-menu-portal cb-select__menu-portal" {...innerProps} style={portalStyle}>
      {children}
    </div>,
    appendTo ?? document.body,
  );
}
